import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const LandingPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate('/wealth-dashboard');
    }
  }, [user, loading, navigate]);

  // Show nothing while checking auth
  if (loading) {
    return null;
  }

  // Don't render landing page if user is authenticated
  if (user) {
    return null;
  }

  return (
    <div dangerouslySetInnerHTML={{
      __html: `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>RetireProApp - Plan for Financial Freedom</title>
            <style>
                :root {
                    /* --- LIGHT THEME PALETTE --- */
                    --background-light: #F5F5F5;
                    --card-background: #FFFFFF;
                    
                    /* Accent Colors */
                    --primary-color: #4F46E5;
                    --secondary-color: #7C3AED;
                    
                    /* Text & Contrast */
                    --text-dark: #1F2937;
                    --text-medium: #4B5563;
                    --text-faded: #6B7280;
                    
                    /* Borders & Shadows */
                    --border-color: #E5E7EB;
                    --shadow-color: rgba(0, 0, 0, 0.08);
                    
                    /* Status Colors */
                    --success-color: #10B981;
                    --highlight-color: #F59E0B;
                    --highlight-color-light: #A78BFA;
                }

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                    background: var(--background-light);
                    color: var(--text-dark);
                    overflow-x: hidden;
                    scroll-behavior: smooth;
                    line-height: 1.6;
                }

                .container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 20px;
                }

                /* Utility */
                .text-center { text-align: center; }

                /* --- Header & Nav (Light Theme) --- */
                header {
                    position: sticky;
                    top: 0;
                    z-index: 1000;
                    padding: 16px 0;
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(8px);
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    border-bottom: 1px solid var(--border-color);
                }

                nav {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .logo {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-size: 24px;
                    font-weight: 700;
                    color: var(--text-dark);
                }

                .logo-icon {
                    width: 44px;
                    height: 44px;
                    background: var(--primary-color);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    color: white;
                }

                .nav-links {
                    display: flex;
                    gap: 32px;
                    list-style: none;
                    align-items: center;
                }

                .nav-links a {
                    color: var(--text-medium);
                    text-decoration: none;
                    transition: all 0.3s;
                    font-weight: 500;
                    font-size: 16px;
                }

                .nav-links a:hover {
                    color: var(--primary-color);
                }

                /* --- Hero Section --- */
                .hero-image-container {
                    width: 100%;
                    height: 550px;
                    position: relative;
                    margin-bottom: 80px;
                    overflow: hidden;
                    border-bottom-left-radius: 30px;
                    border-bottom-right-radius: 30px;
                    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
                }

                .hero-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    position: absolute;
                    top: 0;
                    left: 0;
                    z-index: 5;
                }

                .hero-image-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.2) 50%, rgba(0, 0, 0, 0.5) 100%);
                    z-index: 10;
                }

                .hero-image-content {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    z-index: 20;
                    text-align: center;
                    color: white;
                    width: 90%;
                    max-width: 900px;
                }

                .early-bird-tag {
                    display: inline-block;
                    background: var(--success-color); 
                    color: white;
                    padding: 8px 20px;
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    margin-bottom: 15px;
                }

                .hero-image-content h1 {
                    font-size: 72px;
                    font-weight: 800;
                    margin-bottom: 20px;
                    color: white;
                    line-height: 1.1;
                    text-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
                }
                
                .hero-image-content p {
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 22px;
                    margin-bottom: 40px;
                    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
                }
                .hero-cta {
                    display: flex;
                    gap: 20px;
                    justify-content: center;
                }

                /* --- Button Styles --- */
                .cta-btn, .btn-primary, .btn-secondary {
                    text-decoration: none;
                    font-weight: 600;
                    transition: all 0.3s;
                    border: none;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 28px;
                    border-radius: 10px;
                }

                .cta-btn {
                    background: var(--primary-color);
                    color: white;
                    box-shadow: 0 4px 10px rgba(79, 70, 229, 0.3);
                }

                .btn-primary {
                    background: var(--primary-color);
                    color: white;
                    padding: 18px 40px;
                    box-shadow: 0 8px 20px rgba(79, 70, 229, 0.4);
                }

                .btn-secondary {
                    background: rgba(255, 255, 255, 0.9); 
                    color: var(--primary-color); 
                    padding: 18px 40px;
                    border: 1px solid var(--border-color);
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
                }

                .btn-secondary:hover {
                    background: #F0F0FF; 
                    color: var(--primary-color);
                }

                /* --- Dashboard Preview (Light Theme Styling) --- */
                .dashboard-preview {
                    padding-top: 50px;
                    margin-bottom: 100px;
                }
                
                .section-title {
                    color: var(--text-dark);
                    font-size: 48px;
                    font-weight: 700;
                    margin-bottom: 20px;
                }
                .section-subtitle {
                    color: var(--text-faded);
                    font-size: 20px;
                    margin-bottom: 60px;
                }

                .preview-container {
                    background: var(--card-background);
                    box-shadow: 0 10px 30px var(--shadow-color);
                    border: 1px solid var(--border-color);
                    border-radius: 20px; 
                    padding: 10px;
                    transition: all 0.3s;
                }

                .preview-container:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
                }

                .preview-image {
                    width: 100%;
                    height: auto;
                    display: block;
                    border-radius: 10px;
                }
                
                /* --- Features Section --- */
                .features {
                    background: var(--background-light);
                    padding: 80px 0;
                }

                .features-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
                    gap: 32px;
                }

                .feature-card {
                    background: var(--card-background);
                    border: 1px solid var(--border-color);
                    box-shadow: 0 4px 10px var(--shadow-color);
                    border-radius: 18px;
                    padding: 36px;
                    transition: all 0.3s;
                    min-height: 280px;
                    display: flex;
                    flex-direction: column;
                }

                .feature-card:hover {
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
                    border-color: var(--primary-color);
                    transform: translateY(-5px);
                }

                .feature-icon {
                    font-size: 48px;
                    margin-bottom: 20px;
                    color: var(--primary-color);
                }

                .feature-card h3 { color: var(--text-dark); font-size: 24px; margin-bottom: 14px; }
                .feature-card p { color: var(--text-medium); font-size: 15px; }

                /* --- Pricing Section --- */
                .pricing { padding: 80px 0; }
                .pricing-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
                    gap: 32px;
                    max-width: 1000px;
                    margin: 0 auto;
                }
                .pricing-card {
                    background: var(--card-background);
                    border: 1px solid var(--border-color);
                    box-shadow: 0 5px 15px var(--shadow-color);
                    border-radius: 20px;
                    padding: 40px;
                    transition: all 0.3s;
                    display: grid;
                    grid-template-rows: auto auto auto auto 1fr;
                    gap: 0;
                    min-height: 420px;
                }

                .pricing-card .plan-header { grid-row: 1; margin-bottom: 8px; min-height: 44px; align-items: center; }
                .pricing-card .plan-tagline { grid-row: 2; margin-bottom: 16px; min-height: 48px; }
                .pricing-card .plan-price { grid-row: 3; margin-bottom: 16px; min-height: 72px; display: flex; flex-direction: column; }
                .pricing-card .plan-cta { grid-row: 4; margin-top: 0; margin-bottom: 0; }
                .pricing-card .features-list { grid-row: 5; margin-top: 16px; }

                .pricing-card.featured {
                    background: #F0F3FF; 
                    border-color: var(--primary-color);
                    box-shadow: 0 10px 30px rgba(79, 70, 229, 0.15);
                    transform: scale(1.03); 
                }

                .pricing-card.default {
                    background: #F0F3FF; 
                    border-color: var(--success-color);
                    box-shadow: 0 10px 30px rgba(16, 185, 129, 0.12);
                }

                .plan-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 10px;
                }

                .plan-badge {
                    padding: 8px 18px;
                    border-radius: 20px;
                    font-weight: 700;
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    white-space: nowrap;
                }

                .default-badge {
                    background: var(--success-color);
                    color: white;
                }

                .popular-badge {
                    background: var(--highlight-color);
                    color: white;
                }

                .plan-savings {
                    font-size: 14px;
                    color: var(--success-color);
                    font-weight: 600;
                    margin-top: 8px;
                }

                .plan-name { 
                    color: var(--text-dark); 
                    font-size: 20px;
                    font-weight: 700;
                    margin-bottom: 0;
                    line-height: 1.3;
                }
                
                .plan-tagline { 
                    color: var(--text-medium); 
                    font-size: 15px;
                    line-height: 1.5;
                    margin-bottom: 30px;
                }
                
                .plan-price {
                    margin-bottom: 30px;
                }
                
                .price-amount { 
                    color: var(--text-dark); 
                    font-size: 48px;
                    font-weight: 700;
                    display: flex;
                    align-items: baseline;
                    gap: 5px;
                }
                
                .currency {
                    font-size: 36px;
                }
                
                .price-period { 
                    color: var(--text-faded); 
                    font-size: 18px;
                    font-weight: 400;
                }

                .plan-cta {
                    background: var(--primary-color);
                    color: white;
                    padding: 15px 30px;
                    border-radius: 10px;
                    text-decoration: none;
                    font-weight: 600;
                    text-align: center;
                    transition: all 0.3s;
                    border: none;
                    font-size: 16px;
                    display: inline-block;
                    align-self: start;
                }
                
                .plan-cta.secondary {
                     background: var(--text-dark);
                     color: white;
                }
                
                .plan-cta:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                }

                .features-list { 
                    list-style: none; 
                    padding: 0;
                    margin-top: 20px;
                }
                
                .features-list li {
                    color: var(--text-medium);
                    padding: 12px 0;
                    border-bottom: 1px solid var(--border-color);
                    display: flex;
                    gap: 15px;
                    align-items: center;
                    font-size: 15px;
                }
                
                .features-list li:last-child {
                    border-bottom: none;
                }

                .check-icon { 
                    color: var(--success-color); 
                    font-weight: bold; 
                    font-size: 16px;
                }

                /* --- Social Proof & Testimonials --- */
                .social-proof {
                    background: #F0F3FF;
                    padding: 100px 0;
                    border-top: 1px solid var(--border-color);
                }
                .testimonial-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 30px;
                    margin-top: 50px;
                }
                .testimonial-card {
                    background: var(--card-background);
                    padding: 25px;
                    border-radius: 15px;
                    box-shadow: 0 5px 15px var(--shadow-color);
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }
                .testimonial-card p {
                    font-style: italic;
                    margin-bottom: 15px;
                    color: var(--text-medium);
                }
                .testimonial-footer {
                    border-top: 1px solid var(--border-color);
                    padding-top: 10px;
                }
                .star-rating {
                    color: var(--highlight-color);
                    font-size: 18px;
                    margin-bottom: 5px;
                }
                .testimonial-footer strong {
                    display: block;
                    font-weight: 600;
                    color: var(--text-dark);
                }
                .testimonial-footer span {
                    font-size: 14px;
                    color: var(--text-faded);
                }

                /* --- FAQ Section --- */
                .faq-section {
                    padding: 100px 0;
                }
                .faq-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 30px;
                    margin-top: 50px;
                }
                .faq-item {
                    background: var(--card-background);
                    padding: 25px;
                    border-radius: 12px;
                    border: 1px solid var(--border-color);
                    transition: border-color 0.3s;
                }
                .faq-item:hover {
                    border-color: var(--primary-color);
                }
                .faq-item h3 {
                    font-size: 18px;
                    color: var(--primary-color);
                    margin-bottom: 10px;
                }
                .faq-item p {
                    font-size: 15px;
                    color: var(--text-medium);
                }

                /* --- Final CTA --- */
                .final-cta { padding: 80px 0; }
                .cta-box {
                    background: #F0F3FF; 
                    border: 1px solid var(--primary-color);
                    box-shadow: 0 10px 30px rgba(79, 70, 229, 0.1);
                    border-radius: 25px;
                    padding: 70px 50px;
                    text-align: center;
                }
                .cta-box h2 { color: var(--text-dark); font-size: 42px; margin-bottom: 20px;}
                .cta-box p { color: var(--text-medium); font-size: 20px; margin-bottom: 40px;}

                /* --- Footer --- */
                footer {
                    border-top: 1px solid var(--border-color);
                    background: var(--card-background);
                    padding: 40px 0 20px;
                    text-align: center;
                }
                .footer-content { color: var(--text-faded); font-size: 14px; }
                .footer-links { margin-top: 10px; }
                .footer-links a {
                    color: var(--primary-color);
                    text-decoration: none;
                    margin: 0 10px;
                    font-weight: 500;
                }
                
                /* Responsive Overrides */
                @media (max-width: 768px) {
                    .container { padding: 0 16px; }
                    header { padding: 12px 0; }
                    nav { flex-wrap: wrap; gap: 12px; }
                    .nav-links {
                        display: none;
                    }
                    .cta-btn {
                        min-height: 44px;
                        min-width: 44px;
                        padding: 12px 20px;
                        font-size: 14px;
                    }
                    .hero-image-container { height: 350px; margin-bottom: 40px; }
                    .hero-image-content { width: 95%; padding: 0 10px; }
                    .hero-image-content h1 { font-size: 40px; margin-bottom: 12px; }
                    .hero-image-content p { font-size: 17px; margin-bottom: 24px; }
                    .hero-cta {
                        flex-direction: column;
                        gap: 12px;
                        width: 100%;
                        max-width: 320px;
                        margin: 0 auto;
                    }
                    .hero-cta .btn-primary,
                    .hero-cta .btn-secondary {
                        width: 100%;
                        justify-content: center;
                        min-height: 44px;
                        padding: 14px 24px;
                        font-size: 16px;
                    }
                    .section-title { font-size: 32px; margin-bottom: 12px; }
                    .section-subtitle { font-size: 16px; margin-bottom: 40px; }
                    .pricing { padding: 48px 0; }
                    .pricing-grid {
                        grid-template-columns: 1fr;
                        gap: 24px;
                    }
                    .pricing-card {
                        padding: 24px;
                        min-height: auto;
                    }
                    .pricing-card.featured {
                        transform: none;
                    }
                    .price-amount { font-size: 40px; }
                    .currency { font-size: 28px; }
                    .plan-cta {
                        min-height: 44px;
                        padding: 14px 24px;
                    }
                    .features { padding: 48px 0; }
                    .dashboard-preview { padding-top: 32px; margin-bottom: 60px; }
                    .social-proof { padding: 48px 0; }
                    .testimonial-grid { grid-template-columns: 1fr; margin-top: 32px; }
                    .faq-section { padding: 48px 0; }
                    .faq-grid { grid-template-columns: 1fr; margin-top: 32px; }
                    .faq-item { padding: 20px; }
                    .faq-item h3 { font-size: 16px; }
                    .faq-item p { font-size: 14px; }
                    .final-cta { padding: 48px 0; }
                    .cta-box {
                        padding: 40px 24px;
                    }
                    .cta-box h2 { font-size: 28px; margin-bottom: 16px; }
                    .cta-box p { font-size: 16px; margin-bottom: 24px; }
                    .cta-box .btn-primary {
                        min-height: 44px;
                        padding: 14px 28px;
                        font-size: 16px;
                    }
                    footer { padding: 32px 0 16px; }
                    .footer-links a {
                        display: inline-block;
                        min-height: 44px;
                        line-height: 44px;
                        padding: 0 8px;
                    }
                }
                @media (max-width: 480px) {
                    .container { padding: 0 12px; }
                    .hero-image-content h1 { font-size: 32px; }
                    .hero-image-content p { font-size: 15px; }
                    .section-title { font-size: 26px; }
                    .section-subtitle { font-size: 14px; }
                    .logo span { font-size: 18px; }
                    .logo-icon { width: 36px; height: 36px; font-size: 18px; }
                }
            </style>
        </head>
        <body>
            <header>
                <div class="container">
                    <nav>
                        <div class="logo">
                            <div class="logo-icon">₹</div>
                            <span>RetireProApp</span>
                        </div>
                        <ul class="nav-links">
                            <li><a href="#features">Features</a></li>
                            <li><a href="#social-proof">Reviews</a></li>
                            <li><a href="#pricing">Pricing</a></li>
                            <li><a href="#faq">FAQ</a></li>
                        </ul>
                        <a href="/login" class="cta-btn">Start Free Trial Today →</a>
                    </nav>
                </div>
            </header>

            <div class="hero-image-container">
                <img src="/first_image.png" alt="Aspirational Indian family enjoying early financial freedom in the mountains" class="hero-image">
                
                <div class="hero-image-overlay"></div>
                <div class="hero-image-content">
                    
                    <h1>Plan Your Retirement with Confidence</h1>
                    <p>Visualize and achieve retirement goals with 20-year projections and Monte Carlo simulations.</p>
                    
                    <div class="hero-cta">
                        <a href="/login" class="btn-primary" style="background: var(--success-color); box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);">
                            Start Free Trial Today →
                        </a>
                        <a href="#features" class="btn-secondary">Explore Features</a>
                    </div>
                </div>
            </div>

            <!-- Video Section -->
            <section class="video-section" style="padding: 60px 0; background: var(--card-background);">
                <div class="container">
                    <div class="video-container" style="max-width: 800px; margin: 0 auto; border-radius: 15px; box-shadow: 0 10px 30px var(--shadow-color);">
                        <div style="padding:56.25% 0 0 0;position:relative;">
                            <iframe 
                                src="https://player.vimeo.com/video/1131179925?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" 
                                frameborder="0" 
                                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
                                referrerpolicy="strict-origin-when-cross-origin" 
                                style="position:absolute;top:0;left:0;width:100%;height:100%;" 
                                title="RetirePro Intro Video">
                            </iframe>
                        </div>
                        <script src="https://player.vimeo.com/api/player.js"></script>
                    </div>
                </div>
            </section>

        <section id="dashboard-preview" class="dashboard-preview"> 
            <div class="container">
                <h2 class="section-title text-center">Your Wealth, Visualized</h2> 
                <p class="section-subtitle text-center">See net worth, retirement readiness, and key metrics in one dashboard.</p>
                <div class="preview-container"> 
                    <img src="/Dashboard-Visualize.png" alt="RetireProApp Wealth Dashboard Preview" class="preview-image"> 
                </div> 
            </div> 
        </section>


            <section id="features" class="features">
                <div class="container">
                    <h2 class="section-title text-center">Features to Plan Your Future</h2>
                    <p class="section-subtitle text-center">From real-time financial tracking to advanced scenario planning, RetireProApp gives you the tools to secure your tomorrow.</p>
                    <div class="features-grid">
                        <div class="feature-card">
                            <div class="feature-icon" style="color: var(--highlight-color);">💰</div>
                            <h3>Income & Surplus Routing</h3>
                            <p>Project salary growth, manage passive streams, and **auto-allocate surplus** to assets.</p>
                        </div>
                        <div class="feature-card">
                            <div class="feature-icon" style="color: var(--secondary-color);">💳</div>
                            <h3>Expense Forecasting (20-Year)</h3>
                            <p>Project essential expenses over 20 years, **auto-adjusted for inflation** and life events.</p>
                        </div>
                        <div class="feature-card">
                            <div class="feature-icon" style="color: var(--primary-color);">🏦</div>
                            <h3>Strategic Debt Paydown</h3>
                            <p>Track liabilities, visualize **debt-free timeline**, and model **lump-sum prepayments**.</p>
                        </div>
                        <div class="feature-card">
                            <div class="feature-icon" style="color: var(--success-color);">📈</div>
                            <h3>Diversified Asset Portfolio</h3>
                            <p>Manage investments across asset classes and track liquidity and risk.</p>
                        </div>
                        <div class="feature-card">
                            <div class="feature-icon" style="color: #3B82F6;">📊</div>
                            <h3>Retirement Readiness Score</h3>
                            <p>Get a **real-time score** and track corpus growth for instant financial insight.</p>
                        </div>
                        <div class="feature-card">
                            <div class="feature-icon" style="color: #EF4444;">🎯</div>
                            <h3>Advanced Projections</h3>
                            <p>Run **Monte Carlo simulations** to assess your probability of reaching goals.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section id="pricing" class="pricing">
                <div class="container">
                    <h2 class="section-title text-center">Choose Your Plan</h2>
                    <p class="section-subtitle text-center">Start Free Trial Today — No credit card • Cancel anytime</p>
                    
                    <div class="pricing-grid">
                        <div class="pricing-card default">
                            <div class="plan-header">
                                <h3 class="plan-name">Monthly Subscription</h3>
                                <span class="plan-badge default-badge">RECOMMENDED</span>
                            </div>
                            <p class="plan-tagline">💡 Pay monthly, cancel anytime. Perfect for trying out all features.</p>
                            <div class="plan-price">
                                <div class="price-amount">
                                    <span class="currency">₹</span>99
                                    <span class="price-period">/ month</span>
                                </div>
                            </div>
                            <a href="/login" class="plan-cta primary">Start Free Trial Today</a>
                            <ul class="features-list">
                                <li><span class="check-icon">✓</span> Unlimited Projection Years</li>
                                <li><span class="check-icon">✓</span> Full Cash-Flow Analysis</li>
                                <li><span class="check-icon">✓</span> Retirement Planning & Simulations</li>
                                <li><span class="check-icon">✓</span> Monte Carlo Projections</li>
                                <li><span class="check-icon">✓</span> Asset Allocation Routing</li>
                                <li><span class="check-icon">✓</span> Ad-Hoc & Emergency Planning</li>
                                <li><span class="check-icon">✓</span> Debt Prepayment Modeling</li>
                                <li><span class="check-icon">✓</span> Dedicated Support</li>
                            </ul>
                        </div>

                        <div class="pricing-card featured">
                            <div class="plan-header">
                                <h3 class="plan-name">Annual Subscription</h3>
                                <span class="plan-badge popular-badge">BEST VALUE</span>
                            </div>
                            <p class="plan-tagline">🚀 Save ₹189 per year! The smart choice for serious retirement planning.</p>
                            <div class="plan-price">
                                <div class="price-amount">
                                    <span class="currency">₹</span>999
                                    <span class="price-period">/ year</span>
                                </div>
                                <p class="plan-savings">Save ₹189 compared to monthly</p>
                            </div>
                            <a href="/login" class="plan-cta primary">Start Free Trial Today</a>
                            <ul class="features-list">
                                <li><span class="check-icon">✓</span> ALL Monthly Features, PLUS:</li>
                                <li><span class="check-icon">✓</span> Save ₹189 annually</li>
                                <li><span class="check-icon">✓</span> Priority Support</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section id="social-proof" class="social-proof">
                <div class="container">
                    <h2 class="section-title text-center">Hear From Early Users</h2>
                    <p class="section-subtitle text-center">Clarity always comes before confidence. See what our planners are saying.</p>
                    <div class="testimonial-grid">
                        
                        <div class="testimonial-card">
                            <div class="star-rating">★★★★★</div>
                            <p>"The Monte Carlo simulation feature is a game-changer. It finally showed me the *risk* in my plan, not just the hopeful average. I immediately adjusted my savings."</p>
                            <div class="testimonial-footer">
                                <strong>Arjun S.</strong>
                                <span>Software Engineer, Bangalore</span>
                            </div>
                        </div>
                        
                        <div class="testimonial-card">
                            <div class="star-rating">★★★★★</div>
                            <p>"I love how it takes my Gold and Real Estate assets and puts them in the context of my entire 20-year plan. Truly holistic planning."</p>
                            <div class="testimonial-footer">
                                <strong>Priya K.</strong>
                                <span>Home Entrepreneur, Delhi</span>
                            </div>
                        </div>

                        <div class="testimonial-card">
                            <div class="star-rating">★★★★★</div>
                            <p>"The 4x Margin of Safety is a principle I now live by. It gave me real peace of mind knowing I'm protected against high inflation and market crashes."</p>
                            <div class="testimonial-footer">
                                <strong>Ravi V.</strong>
                                <span>Senior Consultant, Mumbai</span>
                            </div>
                        </div>

                        <div class="testimonial-card">
                            <div class="star-rating">★★★★★</div>
                            <p>"The automatic surplus routing is genius. I used to let my bonuses sit idle; now they automatically get allocated to my highest-priority asset class."</p>
                            <div class="testimonial-footer">
                                <strong>Nita J.</strong>
                                <span>Marketing Manager, Pune</span>
                            </div>
                        </div>

                        <div class="testimonial-card">
                            <div class="star-rating">★★★★★</div>
                            <p>"I finally know my exact debt-free date after modeling a lump-sum payment. That clarity is worth the Premium price alone."</p>
                            <div class="testimonial-footer">
                                <strong>Suresh M.</strong>
                                <span>Small Business Owner, Chennai</span>
                            </div>
                        </div>

                        <div class="testimonial-card">
                            <div class="star-rating">★★★★★</div>
                            <p>"The interface is clean and not intimidating. It breaks down complex financial concepts into simple, actionable steps. Highly recommend for any young professional."</p>
                            <div class="testimonial-footer">
                                <strong>Lata G.</strong>
                                <span>Product Designer, Hyderabad</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="faq" class="faq-section">
                <div class="container">
                    <h2 class="section-title text-center">Frequently Asked Questions</h2>
                    <div class="faq-grid">
                        
                        <div class="faq-item">
                            <h3>1. What exactly is RetireProApp?</h3>
                            <p>RetireProApp is a personal finance and retirement planning tool that helps you see your income, expenses, assets, and loans all in one place — and shows how your wealth will grow or deplete over time. It's DIY (Do-It-Yourself) — not advisory — so you stay in control of your data and decisions.</p>
                        </div>
                        
                        <div class="faq-item">
                            <h3>2. Do I need any financial background to use it?</h3>
                            <p>Not at all. RetireProApp guides you step-by-step like a digital coach. You just enter what you know — your salary, savings, and expenses — and it builds your future picture automatically.</p>
                        </div>
                        
                        <div class="faq-item">
                            <h3>3. How accurate are the projections?</h3>
                            <p>Your projections are based on the data you enter and realistic growth or inflation assumptions. You can adjust these anytime to see "what-if" scenarios. RetireProApp doesn't give investment advice — it helps you visualize and make informed choices.</p>
                        </div>
                        
                        <div class="faq-item">
                            <h3>4. Is my data safe and private?</h3>
                            <p>Absolutely. Your financial data is stored securely and never sold or shared. There are no ads, no product selling, and no hidden tracking. It's your data — you stay in full control.</p>
                        </div>
                        
                        <div class="faq-item">
                            <h3>5. What happens after my free trial?</h3>
                            <p>After your free trial: You'll still be able to view your plan, charts, and dashboards. Editing, adding, or changing data will be locked. Upgrade anytime to restore full editing access instantly.</p>
                        </div>

                        <div class="faq-item">
                            <h3>6. What's the difference between Monthly and Annual?</h3>
                            <p>Monthly gives you full access at ₹99/month — cancel anytime. Annual unlocks the same features at ₹999/year and saves you ₹189. Both include unlimited projections, Monte Carlo simulations, and dedicated support.</p>
                        </div>

                        <div class="faq-item">
                            <h3>7. I'm a financial coach. Can I use this with my clients?</h3>
                            <p>Yes! Our Coach Plan lets you create and manage up to 10 client profiles, view readiness dashboards, and export white-labeled reports. It's perfect for trainers or advisors who want to empower clients with hands-on planning.</p>
                        </div>

                        <div class="faq-item">
                            <h3>8. Do I need a credit card to start the trial?</h3>
                            <p>No credit card is required to start your free trial. Just sign up and begin planning. You can upgrade later only if you find it useful.</p>
                        </div>

                        <div class="faq-item">
                            <h3>9. Does RetireProApp give investment or tax advice?</h3>
                            <p>No. RetireProApp does not provide tax, legal, or investment recommendations. It's a planning and learning tool — helping you understand and manage your financial life independently.</p>
                        </div>

                        <div class="faq-item">
                            <h3>10. What makes RetireProApp different?</h3>
                            <p>Most tools either sell products or drown you in complexity. RetireProApp is designed to teach, guide, and visualize — no jargon, no sales. It focuses on peace of mind — helping you see where you stand and how to improve.</p>
                        </div>

                    </div>
                </div>
            </section>
            
            <section class="final-cta">
                <div class="container">
                    <div class="cta-box">
                        <h2>Know Your Future.</h2>
                        <p>Start Free Trial Today for a secure retirement.</p>
                        <a href="/login" class="btn-primary" style="background: var(--success-color);">
                            Start Free Trial Today →
                        </a>
                    </div>
                </div>
            </section>

            <footer>
                <div class="container">
                    <div class="footer-content">
                        <p>&copy; 2025 RetireProApp. Secure & Encrypted Financial Planning.</p>
                        <p>Contact: <a href="mailto:support@retireproapp.com" style="color: var(--primary-color); text-decoration: none;">support@retireproapp.com</a></p>
                    </div>
                    <div class="footer-links">
                        <a href="/about-us">About Us</a> | 
                        <a href="/privacy-policy">Privacy Policy</a> | 
                        <a href="/terms-of-service">Terms & Conditions</a>
                    </div>
    </div>
            </footer>
        </body>
        </html>
      `
    }} />
  );
};

export default LandingPage;
